import {createHttpClient, DEFAULT_BASE_URL} from "./client";

async function dataUrlToFile(source, fileName) {
  const response = await fetch(source);
  const blob = await response.blob();
  return new File([blob], fileName, {type: blob.type || "image/png"});
}

async function blobUrlToDataUrl(source) {
  const response = await fetch(source);
  if (!response.ok) throw new Error("作品图片读取失败");
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("作品图片读取失败"));
    reader.readAsDataURL(blob);
  });
}

export function createWorksApi(baseUrl) {
  const apiBaseUrl = baseUrl || DEFAULT_BASE_URL;
  const http = createHttpClient(apiBaseUrl);

  return {
    fetchHome(params = {}) {
      return http.get("/quark/bead/ui2/home", {params});
    },
    fetchWorks(params = {}) {
      return http.get("/quark/bead/community/listComment", {params});
    },
    fetchMyWorks(params = {}) {
      return http.get("/quark/bead/community/my", {params});
    },
    fetchFavorites(params = {}) {
      return http.get("/quark/bead/community/queryOperationComment", {params});
    },
    fetchWork(params = {}) {
      return http.get(`/quark/bead/community/commentDetail`, {params});
    },
    fetchImageDataUrl(imageUrl) {
      if (!imageUrl) return Promise.reject(new Error("作品图片地址为空"));
      if (String(imageUrl).startsWith("data:image/")) return Promise.resolve(imageUrl);
      return http.get("/quark/bead/community/image/base64", {params: {imageUrl}});
    },
    saveWork(payload) {
      return http.post("/quark/bead/community/createComment", payload);
    },
    async prepareWorkImage(source) {
      const sourceText = String(source || "");
      if (!sourceText) return "";
      // 作品图片由社区保存接口在服务端统一落盘并转换为 HTTP 地址。
      // /infra/file/upload 属于后台管理鉴权，网站用户的登录令牌不能用于该接口。
      if (sourceText.startsWith("blob:")) return blobUrlToDataUrl(sourceText);
      return sourceText;
    },
    async uploadWorkImage(source, fileName = "bead-work.png") {
      const sourceText = String(source || "");
      if (!sourceText || (!sourceText.startsWith("data:image/") && !sourceText.startsWith("blob:"))) return sourceText;
      const file = await dataUrlToFile(source, fileName);
      const formData = new FormData();
      formData.append("file", file);
      const result = await http.post("/infra/file/upload", formData, {
        headers: {"Content-Type": "multipart/form-data"}
      });
      const url = typeof result === "string" ? result : result?.url || result?.data || "";
      if (!/^https?:\/\//i.test(url)) throw new Error("作品图片上传后未返回有效地址");
      return url;
    },
    fetchTopics() {
      return http.get("/quark/bead/ui2/topics");
    },
    toggleFavorite(params) {
      return http.post(`/quark/bead/community/operationComment`, params);
    },
    deleteWork(params) {
      return http.post(`/quark/bead/community/delComment`, params);
    },
    buildPoster(params = {}) {
      return http.post(`/quark/bead/community/poster`, params);
    },
    reportWork(params = {}) {
      return http.post(`/quark/bead/community/report`, params);
    },
    shareLandingUrl(commentId) {
      // 对用户展示和复制的必须是前台作品地址，不能暴露 /app-api 接口地址。
      // BASE_URL 在生产环境为 /pindou/，在本地开发时也能得到可直接打开的页面。
      const appBase = new URL(process.env.BASE_URL || "/pindou/", window.location.origin);
      return new URL(`works/${encodeURIComponent(commentId)}`, appBase).href;
    },
    shareMetaUrl(commentId) {
      // 社交平台抓取标题、描述和缩略图时仍可使用服务端落地页。
      const base = new URL(apiBaseUrl, window.location.origin);
      const root = base.href.replace(/\/$/, "");
      return `${root}/quark/bead/community/share/${encodeURIComponent(commentId)}`;
    }
  };
}
